import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPLOYMENT_CONTRACT_LABELS,
  EMPLOYMENT_CONTRACTS,
  type EmployeeRequest,
  type EmployeeResponse,
  employeeSchema,
  type EmploymentContract,
  formatIban,
  ibanError,
  type PeopleOptions,
  RELATIONSHIP_LABELS,
  RELATIONSHIPS,
  type Relationship,
  type CollaboratorSubjectChoice,
  splitPersonName,
  TFR_DESTINATION_LABELS,
  TFR_DESTINATIONS,
  type TfrDestination,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { peopleKeys } from './people-ui.js';

interface Form {
  lastName: string;
  firstName: string;
  taxCode: string;
  birthDate: string;
  birthPlace: string;
  address: string;
  phone: string;
  personalEmail: string;
  workEmail: string;
  relationship: Relationship;
  /**
   * The subject of Anagrafica of a collaborator; `supplier`, `company` and the
   * contacts of a company (2.40) known once chosen here.
   */
  subject: {
    id: string;
    name: string;
    supplier?: boolean;
    company?: boolean;
    contacts?: CollaboratorSubjectChoice['contacts'];
  } | null;
  contract: EmploymentContract;
  partTime: boolean;
  partTimePercent: string;
  ccnl: string;
  level: string;
  jobTitle: string;
  roleCode: string;
  hiredOn: string;
  leftOn: string;
  iban: string;
  payingAccountCode: string;
  tfr: TfrDestination;
  pensionFundCode: string;
  fundJoinedOn: string;
  notes: string;
}

function formOf(employee: EmployeeResponse | null, relationship: Relationship): Form {
  return {
    lastName: employee?.lastName ?? '',
    firstName: employee?.firstName ?? '',
    taxCode: employee?.taxCode ?? '',
    birthDate: employee?.birthDate ?? '',
    birthPlace: employee?.birthPlace ?? '',
    address: employee?.address ?? '',
    phone: employee?.phone ?? '',
    personalEmail: employee?.personalEmail ?? '',
    workEmail: employee?.workEmail ?? '',
    relationship: employee?.relationship ?? relationship,
    subject:
      employee?.subjectId && employee.subjectName
        ? {
            id: employee.subjectId,
            name: employee.subjectName,
            company: employee.subjectCompany,
          }
        : null,
    contract: employee?.contract ?? 'indeterminato',
    partTime: employee?.partTimePercent != null,
    partTimePercent: employee?.partTimePercent?.toString() ?? '',
    ccnl: employee?.ccnl ?? '',
    level: employee?.level ?? '',
    jobTitle: employee?.jobTitle ?? '',
    roleCode: employee?.roleCode ?? '',
    hiredOn: employee?.hiredOn ?? '',
    leftOn: employee?.leftOn ?? '',
    iban: employee?.iban ? formatIban(employee.iban) : '',
    payingAccountCode: employee?.payingAccountCode ?? '',
    tfr: employee?.tfr ?? 'azienda',
    pensionFundCode: employee?.pensionFundCode ?? '',
    fundJoinedOn: employee?.fundJoinedOn ?? '',
    notes: employee?.notes ?? '',
  };
}

/** «Nuovo soggetto» of Anagrafica, back to Persone with the subject chosen (2.39). */
function newSubjectPath(query: string): string {
  const text = query.trim();
  const params = new URLSearchParams({
    ruolo: 'F',
    per: 'collaboratore',
    ritorno: '/organizzazione/persone',
  });
  if (/^\d{11}$/.test(text)) params.set('piva', text);
  else if (/^[A-Za-z0-9]{16}$/.test(text)) params.set('cf', text.toUpperCase());
  else if (text) params.set('name', text);
  return `/anagrafica/nuovo?${params.toString()}`;
}

/**
 * A collaborator starts from Anagrafica (2.39): the subject is searched by name,
 * VAT number or tax code among those the user sees; a new one is added there.
 */
function SubjectSearch({ onPick }: { onPick: (choice: CollaboratorSubjectChoice) => void }) {
  const canAddSubject = useCan('anagrafica.write');
  const [query, setQuery] = useState('');
  const found = useQuery({
    queryKey: ['people', 'subject-choices', query.trim()],
    queryFn: () =>
      apiRequest<CollaboratorSubjectChoice[]>(
        `/people/subject-choices?q=${encodeURIComponent(query.trim())}`,
      ),
    enabled: query.trim().length >= 2,
  });
  return (
    <div className="card stack">
      <h3>Chi è in Anagrafica?</h3>
      <Field label="Cerca per nome, partita IVA o codice fiscale">
        <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} />
      </Field>
      {found.data && found.data.length > 0 && (
        <ul className="plain-list">
          {found.data.map((subject) => (
            <li key={subject.id}>
              <button
                type="button"
                className="link"
                style={{ textAlign: 'left' }}
                onClick={() => onPick(subject)}
              >
                <strong>{subject.name}</strong>
              </button>
              {subject.vatNumber && <span className="muted"> · P.IVA {subject.vatNumber}</span>}
              {subject.taxCode && subject.taxCode !== subject.vatNumber && (
                <span className="muted"> · CF {subject.taxCode}</span>
              )}{' '}
              {subject.supplier ? (
                <span className="pill ok">Fornitore</span>
              ) : (
                <span className="pill muted">non è fornitore</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {found.data && found.data.length === 0 && (
        <p className="muted">Nessun soggetto trovato in Anagrafica.</p>
      )}
      {canAddSubject ? (
        <p className="muted">
          Non c’è? <Link to={newSubjectPath(query)}>Aggiungilo in Anagrafica…</Link> Si apre «Nuovo
          soggetto» con il ruolo Fornitore già scelto; salvato, torni qui con il soggetto
          selezionato.
        </p>
      ) : (
        <p className="muted">
          Non c’è? Chiedi a chi gestisce l’Anagrafica di inserirlo come fornitore, poi cercalo qui.
        </p>
      )}
    </div>
  );
}

/** New person, or the change of one (07/10/2026; collaborators 2.38). */
export function EmployeeDialog({
  employee,
  relationship = 'dipendente',
  initialSubjectId = null,
  onClose,
  onSaved,
}: {
  employee: EmployeeResponse | null;
  /** What a new person is: «Nuovo dipendente» or «Nuovo collaboratore» (2.40). */
  relationship?: Relationship;
  /** A new collaborator from the subject just added in Anagrafica (2.39). */
  initialSubjectId?: string | null;
  onClose: () => void;
  onSaved?: (saved: EmployeeResponse) => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<Form>(() =>
    formOf(employee, initialSubjectId ? 'collaboratore' : relationship),
  );
  // The contact of the company the person was taken from (2.40); '' = written by hand.
  const [contactIndex, setContactIndex] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const options = useQuery({
    queryKey: peopleKeys.options,
    queryFn: () => apiRequest<PeopleOptions>('/people/options'),
  });
  const set =
    <K extends keyof Form>(key: K) =>
    (value: Form[K]) =>
      setForm({ ...form, [key]: value });
  /**
   * The subject chosen: what the person lacks comes from Anagrafica, and stays
   * editable. A company gives nothing of the person (2.40): they come from one
   * of its contacts or are written by hand.
   */
  const pick = async (choice: CollaboratorSubjectChoice) => {
    const full = await apiRequest<CollaboratorSubjectChoice>(
      `/people/subject-choices/${choice.id}`,
    ).catch(() => choice);
    const subject = {
      id: full.id,
      name: full.name,
      supplier: full.supplier,
      company: full.company,
      contacts: full.contacts,
    };
    if (full.company) {
      setContactIndex('');
      setForm((current) => ({ ...current, subject }));
      return;
    }
    const names = splitPersonName(full.name);
    setForm((current) => ({
      ...current,
      subject,
      lastName: current.lastName || names.lastName,
      firstName: current.firstName || names.firstName,
      taxCode: current.taxCode || (full.taxCode && full.taxCode.length === 16 ? full.taxCode : ''),
      phone: current.phone || (full.phone ?? ''),
      personalEmail: current.personalEmail || (full.email ?? ''),
      address: current.address || (full.address ?? ''),
    }));
  };
  useEffect(() => {
    if (!initialSubjectId) return;
    void apiRequest<CollaboratorSubjectChoice>(`/people/subject-choices/${initialSubjectId}`)
      .then(pick)
      .catch(() => undefined);
    // Only once, for the subject just added.
  }, [initialSubjectId]);
  /** The person from a contact of the company (2.40): names, e-mail and phone, still editable. */
  const pickContact = (value: string) => {
    setContactIndex(value);
    const contact = form.subject?.contacts?.[Number(value)];
    if (value === '' || !contact) return;
    const names = splitPersonName(contact.name);
    setForm((current) => ({
      ...current,
      lastName: names.lastName,
      firstName: names.firstName,
      workEmail: contact.email ?? current.workEmail,
      phone: contact.phone ?? current.phone,
    }));
  };
  // A collaborator starts from Anagrafica: the rest comes once the subject is chosen (2.39).
  const searching = form.relationship === 'collaboratore' && !form.subject;
  // Through a company the tax code is optional (2.40): the API checks it on the subject.
  const throughCompany = form.relationship === 'collaboratore' && form.subject?.company === true;
  const text = (key: keyof Form) => ({
    value: form[key] as string,
    onChange: (event: { target: { value: string } }) => set(key)(event.target.value as never),
  });
  const bodyOf = (): EmployeeRequest => ({
    lastName: form.lastName,
    firstName: form.firstName,
    taxCode: form.taxCode,
    birthDate: form.birthDate || null,
    birthPlace: form.birthPlace,
    address: form.address,
    phone: form.phone,
    personalEmail: form.personalEmail.trim() || null,
    workEmail: form.workEmail.trim() || null,
    relationship: form.relationship,
    subjectId: form.relationship === 'collaboratore' ? (form.subject?.id ?? null) : null,
    contract: form.contract,
    partTimePercent: form.partTime ? Number(form.partTimePercent) || 0 : null,
    ccnl: form.ccnl,
    level: form.level,
    jobTitle: form.jobTitle,
    roleCode: form.roleCode || null,
    hiredOn: form.hiredOn,
    leftOn: form.leftOn || null,
    iban: form.iban,
    payingAccountCode: form.payingAccountCode || null,
    tfr: form.tfr,
    pensionFundCode: form.tfr === 'fondo' ? form.pensionFundCode || null : null,
    fundJoinedOn: form.tfr === 'fondo' ? form.fundJoinedOn || null : null,
    notes: form.notes,
  });
  const save = useMutation({
    mutationFn: (body: EmployeeRequest) =>
      employee
        ? apiRequest<EmployeeResponse>(`/people/employees/${employee.id}`, { method: 'PUT', body })
        : apiRequest<EmployeeResponse>('/people/employees', { method: 'POST', body }),
    onError: (error) => showServerError(error, setErrors, setFormError),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: peopleKeys.all });
      onSaved?.(saved);
      onClose();
    },
  });
  const ibanProblem = form.iban.trim() ? ibanError(form.iban) : null;
  const ready =
    form.lastName.trim() &&
    form.firstName.trim() &&
    (form.relationship === 'collaboratore' || form.taxCode.trim()) &&
    form.hiredOn;

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>
          {employee
            ? `${employee.lastName} ${employee.firstName}`
            : form.relationship === 'collaboratore'
              ? 'Nuovo collaboratore'
              : 'Nuovo dipendente'}
        </h2>
        {employee && (
          <Field label="Rapporto">
            <select
              value={form.relationship}
              onChange={(event) => set('relationship')(event.target.value as Relationship)}
            >
              {RELATIONSHIPS.map((item) => (
                <option key={item} value={item}>
                  {RELATIONSHIP_LABELS[item]}
                </option>
              ))}
            </select>
          </Field>
        )}
        {searching && <SubjectSearch onPick={(choice) => void pick(choice)} />}
        {!searching && (
          <>
            {form.relationship === 'collaboratore' && form.subject && (
              <div className="card stack">
                <div className="row between">
                  <span>
                    Fornitore in Anagrafica: <strong>{form.subject.name}</strong>{' '}
                    {form.subject.company && <span className="pill muted">Società</span>}
                  </span>
                  <button type="button" className="link small" onClick={() => set('subject')(null)}>
                    Cambia
                  </button>
                </div>
                {form.subject.company && (
                  <>
                    <p className="muted">
                      È una società: indica la persona che lavora per K-Rev tramite la società. I
                      dati della persona non vengono presi dalla società.
                    </p>
                    {(form.subject.contacts?.length ?? 0) > 0 && (
                      <Field label="Chi è la persona?">
                        <select
                          value={contactIndex}
                          onChange={(event) => pickContact(event.target.value)}
                        >
                          <option value="">Un’altra persona, scritta a mano</option>
                          {form.subject.contacts!.map((contact, index) => (
                            <option key={index} value={String(index)}>
                              {contact.name}
                              {contact.title ? ` — ${contact.title}` : ''}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}
                  </>
                )}
              </div>
            )}
            <h3>Anagrafica</h3>
            <div className="form-grid">
              <Field label="Cognome" error={errors.lastName}>
                <input {...text('lastName')} />
              </Field>
              <Field label="Nome" error={errors.firstName}>
                <input {...text('firstName')} />
              </Field>
              <Field
                label={
                  form.relationship === 'collaboratore' && (throughCompany || !form.subject)
                    ? 'Codice fiscale (facoltativo)'
                    : 'Codice fiscale'
                }
                error={errors.taxCode}
              >
                <input {...text('taxCode')} autoComplete="off" maxLength={16} />
              </Field>
              <Field label="Data di nascita" error={errors.birthDate}>
                <input type="date" {...text('birthDate')} />
              </Field>
              <Field label="Luogo di nascita" error={errors.birthPlace}>
                <input {...text('birthPlace')} />
              </Field>
              <Field label="Residenza" error={errors.address}>
                <input {...text('address')} />
              </Field>
              <Field label="Telefono" error={errors.phone}>
                <input {...text('phone')} />
              </Field>
              <Field label="E-mail personale" error={errors.personalEmail}>
                <input type="email" {...text('personalEmail')} />
              </Field>
              <Field label="E-mail aziendale" error={errors.workEmail}>
                <input type="email" {...text('workEmail')} />
              </Field>
            </div>
            {form.relationship === 'collaboratore' ? (
              <>
                <h3>Collaborazione</h3>
                <div className="form-grid">
                  {errors.subjectId && <p className="error">{errors.subjectId}</p>}
                  {form.subject?.supplier === false && (
                    <p className="notice">
                      Il soggetto non è ancora un <strong>fornitore</strong>: il collaboratore
                      emette fatture, quindi serve il ruolo Fornitore.{' '}
                      <Link to={`/anagrafica/${form.subject.id}?ruolo=nuovo`}>
                        Aggiungi il ruolo in Anagrafica…
                      </Link>
                    </p>
                  )}
                  <Field label="Mansione" error={errors.jobTitle}>
                    <input {...text('jobTitle')} />
                  </Field>
                  <Field label="Ruolo aziendale" error={errors.roleCode}>
                    <select {...text('roleCode')}>
                      <option value="">—</option>
                      {options.data?.roles.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Inizio della collaborazione" error={errors.hiredOn}>
                    <input type="date" {...text('hiredOn')} />
                  </Field>
                  <Field label="Fine" error={errors.leftOn}>
                    <input type="date" {...text('leftOn')} />
                  </Field>
                  <Field label="Note" error={errors.notes}>
                    <input {...text('notes')} />
                  </Field>
                </div>
              </>
            ) : (
              <>
                <h3>Rapporto di lavoro</h3>
                <div className="form-grid">
                  <Field label="Contratto">
                    <select
                      value={form.contract}
                      onChange={(event) =>
                        set('contract')(event.target.value as EmploymentContract)
                      }
                    >
                      {EMPLOYMENT_CONTRACTS.map((item) => (
                        <option key={item} value={item}>
                          {EMPLOYMENT_CONTRACT_LABELS[item]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Orario">
                    <select
                      value={form.partTime ? 'part' : 'full'}
                      onChange={(event) => set('partTime')(event.target.value === 'part')}
                    >
                      <option value="full">Tempo pieno</option>
                      <option value="part">Part-time</option>
                    </select>
                  </Field>
                  {form.partTime && (
                    <Field label="Part-time %" error={errors.partTimePercent}>
                      <input inputMode="numeric" {...text('partTimePercent')} />
                    </Field>
                  )}
                  <Field label="CCNL" error={errors.ccnl}>
                    <input {...text('ccnl')} />
                  </Field>
                  <Field label="Livello" error={errors.level}>
                    <input {...text('level')} />
                  </Field>
                  <Field label="Mansione" error={errors.jobTitle}>
                    <input {...text('jobTitle')} />
                  </Field>
                  <Field label="Ruolo aziendale" error={errors.roleCode}>
                    <select {...text('roleCode')}>
                      <option value="">—</option>
                      {options.data?.roles.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Assunzione" error={errors.hiredOn}>
                    <input type="date" {...text('hiredOn')} />
                  </Field>
                  <Field label="Cessazione" error={errors.leftOn}>
                    <input type="date" {...text('leftOn')} />
                  </Field>
                </div>
                <h3>Pagamenti e TFR</h3>
                <div className="form-grid">
                  <Field label="IBAN dello stipendio" error={ibanProblem ?? errors.iban}>
                    <input {...text('iban')} autoComplete="off" />
                  </Field>
                  <Field label="Pagato dal conto" error={errors.payingAccountCode}>
                    <select {...text('payingAccountCode')}>
                      <option value="">—</option>
                      {options.data?.accounts.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Destinazione TFR">
                    <select
                      value={form.tfr}
                      onChange={(event) => set('tfr')(event.target.value as TfrDestination)}
                    >
                      {TFR_DESTINATIONS.map((item) => (
                        <option key={item} value={item}>
                          {TFR_DESTINATION_LABELS[item]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {form.tfr === 'fondo' && (
                    <>
                      <Field
                        label="Fondo pensione"
                        error={errors.pensionFundCode}
                        hint={
                          options.data?.funds.length === 0 ? (
                            <>
                              Nessun fondo: aggiungilo in{' '}
                              <Link to="/tabelle/fondi-pensione">Tabelle › Fondi pensione</Link>.
                            </>
                          ) : undefined
                        }
                      >
                        <select {...text('pensionFundCode')}>
                          <option value="">—</option>
                          {options.data?.funds.map((item) => (
                            <option key={item.code} value={item.code}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Adesione dal" error={errors.fundJoinedOn}>
                        <input type="date" {...text('fundJoinedOn')} />
                      </Field>
                    </>
                  )}
                  <Field label="Note" error={errors.notes}>
                    <input {...text('notes')} />
                  </Field>
                </div>
              </>
            )}
          </>
        )}
        {formError && <p className="error">{formError}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={
              save.isPending ||
              searching ||
              !ready ||
              (form.relationship === 'dipendente' && Boolean(ibanProblem))
            }
            onClick={() => {
              const body = bodyOf();
              const checked = validate(employeeSchema, body);
              setErrors(checked.errors);
              setFormError(
                Object.keys(checked.errors).length ? 'Correggi i campi segnati in rosso.' : '',
              );
              if (!Object.keys(checked.errors).length) save.mutate(body);
            }}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
